import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calculator as CalcIcon, TrendingUp, Landmark, PiggyBank, Users, ShieldCheck, Target, Clock, CheckCircle2, ArrowRight, ExternalLink } from "lucide-react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  ageFromDob, buildFramework, CALC_DESCRIPTIONS, CALC_LABELS,
  fmtMoney, isRecommended, nextStep, nextStepSentence, summarizeResult,
  type CalcType, type SavedResult, type StrategyData,
} from "@/lib/strategy-center";
import { CalcByType } from "@/components/strategy/Calculators";

export const Route = createFileRoute("/client/calculators")({
  head: () => ({ meta: [{ title: "Family Strategy Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Strategy Center" nav={CLIENT_NAV} tourKey="client_tour">
        <StrategyCenter />
      </PortalShell>
    </ProtectedRoute>
  ),
});

const CALC_TYPES: CalcType[] = ["dime", "compound", "retirement", "estate"];
const CALC_ICONS: Record<CalcType, typeof CalcIcon> = {
  dime: ShieldCheck,
  compound: TrendingUp,
  retirement: PiggyBank,
  estate: Landmark,
};
const CALC_FULL_ROUTE: Record<CalcType, string> = {
  dime: "/tools/dime",
  compound: "/tools/compound",
  retirement: "/tools/retirement",
  estate: "/tools/estate",
};

function useStrategyData(userId: string | undefined) {
  return useQuery<StrategyData>({
    queryKey: ["strategy-center", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data: clientRow } = await supabase
        .from("clients")
        .select("id, dob, household_size, retirement_target_age, estimated_net_worth")
        .eq("user_id", userId!)
        .maybeSingle();

      const [membersRes, policiesRes, recentRes, apptRes] = await Promise.all([
        clientRow?.id
          ? supabase.from("household_members").select("id, full_name, relationship, dob, is_dependent").eq("client_id", clientRow.id)
          : Promise.resolve({ data: [] as never[], error: null }),
        clientRow?.id
          ? supabase.from("policies").select("id, product_type, face_amount, status").eq("client_id", clientRow.id).eq("status", "active")
          : Promise.resolve({ data: [] as never[], error: null }),
        supabase.from("save_calculator_results")
          .select("id, calculator_type, inputs, result, created_at")
          .eq("user_id", userId!).order("created_at", { ascending: false }).limit(25),
        clientRow?.id
          ? supabase.from("appointments").select("id", { count: "exact", head: true }).eq("client_id", clientRow.id)
          : Promise.resolve({ count: 0 } as { count: number }),
      ]);

      const recent = (recentRes.data ?? []) as unknown as SavedResult[];
      const lastByType: Partial<Record<CalcType, SavedResult>> = {};
      for (const r of recent) {
        if (!lastByType[r.calculator_type]) lastByType[r.calculator_type] = r;
      }

      return {
        client: clientRow as StrategyData["client"],
        members: (membersRes.data ?? []) as StrategyData["members"],
        policies: (policiesRes.data ?? []) as StrategyData["policies"],
        lastByType,
        recent: recent.slice(0, 5),
        appointmentCount: (apptRes as { count: number | null }).count ?? 0,
      };
    },
  });
}

function StrategyCenter() {
  const { user } = useAuth();
  const { data, isLoading, refetch } = useStrategyData(user?.id);
  const [openCalc, setOpenCalc] = useState<{ type: CalcType; inputs?: Record<string, number> } | null>(null);

  // strategy_center.viewed — once per mount
  useEffect(() => {
    if (!user?.id) return;
    supabase.from("activity_logs").insert({
      user_id: user.id, action: "strategy_center.viewed", target_type: "page",
    });
  }, [user?.id]);

  const framework = useMemo(() => (data ? buildFramework(data) : []), [data]);
  const next = useMemo(() => (framework.length ? nextStep(framework) : null), [framework]);

  // strategy_recommendation.shown — once per next-step computation
  useEffect(() => {
    if (!user?.id || !next) return;
    supabase.from("activity_logs").insert({
      user_id: user.id, action: "strategy_recommendation.shown",
      target_type: "framework_step", metadata: { step: next.num, label: next.label },
    });
  }, [user?.id, next?.num]); // eslint-disable-line react-hooks/exhaustive-deps

  const openType = (type: CalcType, inputs?: Record<string, number>, resultId?: string) => {
    if (user?.id && resultId) {
      supabase.from("activity_logs").insert({
        user_id: user.id, action: "calculator_history.opened",
        target_type: "save_calculator_results", target_id: resultId,
        metadata: { calculator_type: type },
      });
    }
    setOpenCalc({ type, inputs });
  };

  return (
    <>
      <PortalPage
        eyebrow="Planning Tools"
        title="Your Family's Strategy Center"
        intro="Tools, calculations, and a guided framework for the decisions ahead. Each number you generate becomes part of your family's plan."
      >
        {isLoading || !data ? (
          <div className="py-12 text-center text-charcoal-500 font-sans text-[14px]">
            Preparing your strategy center…
          </div>
        ) : (
          <div className="space-y-0">
            <SectionGlance data={data} onOpenDime={() => openType("dime")} />
            <SectionTools data={data} onOpen={openType} />
            <SectionFramework
              framework={framework}
              next={next!}
              onOpenCalc={(t) => openType(t)}
            />
            <SectionHistory data={data} onReopen={(r) => openType(r.calculator_type, r.inputs, r.id)} />
            <SectionCTA />
          </div>
        )}
      </PortalPage>

      {openCalc && (
        <CalcByType
          type={openCalc.type}
          userId={user?.id}
          open={true}
          initialInputs={openCalc.inputs}
          onOpenChange={(o) => !o && setOpenCalc(null)}
          onSaved={() => { refetch(); }}
        />
      )}
    </>
  );
}

/* ---------- Section 1: Family at a Glance ---------- */
function SectionGlance({ data, onOpenDime }: { data: StrategyData; onOpenDime: () => void }) {
  const incomplete = !data.client?.dob && data.members.length === 0;
  if (incomplete) {
    return (
      <section className="bg-white border border-bronze-600/20 rounded-[4px] p-8 my-8 text-center">
        <p className="font-display text-navy-800 text-[22px]">
          Complete your profile to unlock personalized insights
        </p>
        <p className="mt-3 font-sans text-[14px] text-charcoal-500 max-w-xl mx-auto">
          A few details about your household — birthdays, dependents, target retirement age — let us tailor every recommendation that follows.
        </p>
        <div className="mt-5">
          <a href="/client/snapshot">
            <PrimaryButton>Update profile →</PrimaryButton>
          </a>
        </div>
      </section>
    );
  }

  const adults = data.members.filter((m) => !m.is_dependent && m.relationship !== "child").length + 1; // primary + non-dependent
  const children = data.members.filter((m) => m.relationship === "child").length;
  const dependents = data.members.filter((m) => m.is_dependent).length;

  const activeFace = data.policies.reduce((s, p) => s + (Number(p.face_amount) || 0), 0);
  const policyTypes = Array.from(new Set(data.policies.map((p) => p.product_type).filter(Boolean))) as string[];

  const dime = data.lastByType.dime;
  const dimeNeed = dime ? Number((dime.result?.value as number) ?? 0) : null;
  const gap = dimeNeed !== null ? activeFace - dimeNeed : null;

  const age = ageFromDob(data.client?.dob);
  const targetAge = data.client?.retirement_target_age ?? 65;
  const yearsToRetire = age !== null ? Math.max(0, targetAge - age) : null;
  const retire = data.lastByType.retirement;
  const monthly = retire ? Number((retire.result?.value as number) ?? 0) / 12 : null;

  return (
    <section className="py-12">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi
          icon={Users} label="Family Composition"
          value={`${adults} adult${adults !== 1 ? "s" : ""}`}
          sub={`${children} children · ${dependents} dependents`}
          linkText="Update profile →" linkTo="/client/snapshot"
        />
        <Kpi
          icon={ShieldCheck} label="Active Protection"
          value={activeFace > 0 ? fmtMoney(activeFace) : "—"}
          sub={data.policies.length > 0
            ? `${data.policies.length} ${data.policies.length === 1 ? "policy" : "policies"}${policyTypes.length ? ` · ${policyTypes.join(", ")}` : ""}`
            : "No active policies on file"}
          linkText="View policies →" linkTo="/client/policies"
        />
        <Kpi
          icon={Target} label="Coverage Need (DIME)"
          value={dimeNeed !== null ? fmtMoney(dimeNeed) : "Not yet run"}
          sub={
            gap === null
              ? "Quantify your family's protection target"
              : gap >= 0
                ? `${fmtMoney(gap)} above need`
                : `${fmtMoney(Math.abs(gap))} gap`
          }
          actionText={dimeNeed === null ? "Take the DIME Calculator →" : undefined}
          onAction={dimeNeed === null ? onOpenDime : undefined}
        />
        <Kpi
          icon={Clock} label="Retirement Horizon"
          value={yearsToRetire !== null ? `${yearsToRetire} yrs` : "Set your DOB"}
          sub={
            monthly !== null
              ? `${fmtMoney(monthly)}/month projected`
              : yearsToRetire !== null
                ? `Target age ${targetAge}`
                : "Add your birthday in settings"
          }
          linkText={yearsToRetire === null ? "Update profile →" : undefined}
          linkTo={yearsToRetire === null ? "/client/snapshot" : undefined}
        />
      </div>
    </section>
  );
}

function Kpi({
  icon: Icon, label, value, sub, linkText, linkTo, actionText, onAction,
}: {
  icon: typeof CalcIcon;
  label: string;
  value: string;
  sub: string;
  linkText?: string;
  linkTo?: string;
  actionText?: string;
  onAction?: () => void;
}) {
  return (
    <article className="bg-white border border-bronze-600/20 rounded-[4px] p-5 shadow-soft flex flex-col">
      <div className="flex items-center gap-2 text-bronze-600">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em]">{label}</p>
      </div>
      <p className="mt-3 font-display text-navy-800 text-[26px] leading-tight">{value}</p>
      <p className="mt-1 font-sans text-[12px] text-charcoal-500">{sub}</p>
      <div className="mt-3 pt-3 border-t border-bronze-600/30 text-[12px]">
        {linkText && linkTo && (
          <a href={linkTo} className="text-bronze-600 hover:text-bronze-400 font-medium">{linkText}</a>
        )}
        {actionText && onAction && (
          <button onClick={onAction} className="text-bronze-600 hover:text-bronze-400 font-medium">{actionText}</button>
        )}
      </div>
    </article>
  );
}

/* ---------- Section 2: Tools ---------- */
function SectionTools({ data, onOpen }: { data: StrategyData; onOpen: (t: CalcType) => void }) {
  const sorted = useMemo(() => {
    return [...CALC_TYPES].sort((a, b) => {
      const ra = isRecommended(a, data) ? 0 : 1;
      const rb = isRecommended(b, data) ? 0 : 1;
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b);
    });
  }, [data]);

  return (
    <section className="bg-cream-50 -mx-4 md:-mx-8 px-4 md:px-8 py-16 my-8">
      <div className="max-w-6xl mx-auto">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">— Recommended Tools —</p>
        <h2 className="mt-2 font-display text-navy-800 text-[32px]">Smart calculators for your situation.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {sorted.map((type) => (
            <ToolCard key={type} type={type} data={data} onOpen={() => onOpen(type)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ToolCard({ type, data, onOpen }: { type: CalcType; data: StrategyData; onOpen: () => void }) {
  const Icon = CALC_ICONS[type];
  const rec = isRecommended(type, data);
  const last = data.lastByType[type];
  return (
    <article className="bg-white border border-bronze-600/20 border-t-2 border-t-bronze-600 rounded-[4px] p-6 shadow-soft flex flex-col">
      <div className="flex items-start justify-between">
        <Icon className="h-7 w-7 text-bronze-600" strokeWidth={1.5} />
        {rec && (
          <span className="bg-bronze-600 text-navy-800 text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-[2px]">
            Recommended for you
          </span>
        )}
      </div>
      <h3 className="mt-4 font-display text-navy-800 text-[22px]">{CALC_LABELS[type]}</h3>
      <p className="mt-2 font-sans text-[13px] text-charcoal-500 flex-1">{CALC_DESCRIPTIONS[type]}</p>
      {last && (
        <p className="mt-3 font-sans text-[12px] text-charcoal-500">
          Last run {new Date(last.created_at).toLocaleDateString()} · Result: <span className="text-navy-800 font-semibold">{summarizeResult(last)}</span>
        </p>
      )}
      <div className="mt-4 flex items-center justify-between gap-3">
        <PrimaryButton onClick={onOpen}>{last ? "Reopen" : "Begin →"}</PrimaryButton>
        <a href={CALC_FULL_ROUTE[type]} className="text-[12px] text-bronze-600 hover:text-bronze-400 inline-flex items-center gap-1">
          Open full page <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </article>
  );
}

/* ---------- Section 3: Framework ---------- */
function SectionFramework({
  framework, next, onOpenCalc,
}: {
  framework: ReturnType<typeof buildFramework>;
  next: ReturnType<typeof buildFramework>[number];
  onOpenCalc: (t: CalcType) => void;
}) {
  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">— The Framework —</p>
        <h2 className="mt-2 font-display text-navy-800 text-[32px]">How your numbers become a strategy.</h2>

        <ol className="mt-10 flex flex-col md:flex-row md:items-start gap-6 md:gap-0">
          {framework.map((s, i) => (
            <li key={s.num} className="flex md:flex-col items-start md:items-center gap-4 md:gap-3 flex-1 md:text-center relative">
              <div className="relative flex-shrink-0">
                <div className={
                  `w-14 h-14 rounded-full flex items-center justify-center font-display text-[22px] border-2 ` +
                  (s.complete
                    ? "bg-navy-800 text-cream-50 border-bronze-600"
                    : "bg-cream-50 text-navy-800 border-bronze-600/50")
                }>
                  {s.num}
                </div>
                {s.complete && (
                  <CheckCircle2 className="absolute -bottom-1 -right-1 h-5 w-5 text-[#0E7C5A] bg-cream-50 rounded-full" />
                )}
              </div>
              <div className="md:mt-2 flex-1">
                <p className="font-display text-navy-800 text-[18px]">{s.label}</p>
                <p className="font-sans italic text-[12px] text-charcoal-500">{s.description}</p>
              </div>
              {i < framework.length - 1 && (
                <span className="hidden md:block absolute top-7 left-[calc(50%+1.75rem)] right-[-50%] h-px bg-bronze-600/40" aria-hidden />
              )}
            </li>
          ))}
        </ol>

        <div className="mt-12 bg-white border border-bronze-600/20 rounded-[4px] p-6">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">— Your next step —</p>
          <p className="mt-3 font-display text-navy-800 text-[22px]">{nextStepSentence(next)}</p>
          <div className="mt-4">
            {next.openCalc ? (
              <PrimaryButton onClick={() => onOpenCalc(next.openCalc!)}>
                {next.label} <ArrowRight className="inline h-4 w-4 ml-1" />
              </PrimaryButton>
            ) : (
              <a href={next.href!}>
                <PrimaryButton>{next.label} →</PrimaryButton>
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 4: History ---------- */
function SectionHistory({ data, onReopen }: { data: StrategyData; onReopen: (r: SavedResult) => void }) {
  return (
    <section className="bg-cream-50 -mx-4 md:-mx-8 px-4 md:px-8 py-16 my-8">
      <div className="max-w-6xl mx-auto">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">— Saved Results —</p>
        <h2 className="mt-2 font-display text-navy-800 text-[32px]">Your recent calculations.</h2>

        {data.recent.length === 0 ? (
          <p className="mt-6 font-sans text-[14px] text-charcoal-500">
            You haven't saved any calculations yet. Start with one of the tools above.
          </p>
        ) : (
          <ul className="mt-6 divide-y divide-bronze-600/20 bg-white border border-bronze-600/20 rounded-[4px]">
            {data.recent.map((r) => {
              const Icon = CALC_ICONS[r.calculator_type];
              return (
                <li key={r.id}>
                  <button
                    onClick={() => onReopen(r)}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-cream-50 transition-colors"
                  >
                    <Icon className="h-5 w-5 text-bronze-600 flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-navy-800 text-[16px]">{CALC_LABELS[r.calculator_type]}</p>
                      <p className="font-sans text-[12px] text-charcoal-500">
                        {summarizeResult(r)} · {new Date(r.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="text-bronze-600 text-[12px] font-medium">Reopen →</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

/* ---------- Section 5: CTA ---------- */
function SectionCTA() {
  return (
    <section className="bg-navy-900 text-cream-50 -mx-4 md:-mx-8 px-4 md:px-8 py-16 text-center rounded-[4px] my-8">
      <h2 className="font-display text-cream-50 text-[32px] max-w-3xl mx-auto leading-tight">
        Numbers are powerful. A strategy is what brings them to life.
      </h2>
      <p className="mt-5 font-sans text-[15px] text-cream-50/80 max-w-[720px] mx-auto leading-relaxed">
        Your Family Wealth Strategist works with you to translate these tools into a written plan tailored to your specific family, goals, and timeline. The gaps and opportunities you can't see in spreadsheets become clear in conversation.
      </p>
      <div className="mt-7">
        <a href="/book?source=strategy_center">
          <PrimaryButton>Book Your Strategy Session →</PrimaryButton>
        </a>
      </div>
    </section>
  );
}
